import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useRegisterSW } from 'virtual:pwa-register/react'

const bannerStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 99999,
  backgroundColor: '#051C36',
  color: '#ffffff',
  padding: '16px 20px',
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
  borderTop: '1px solid #1e3a8a',
}

const subtextStyle: React.CSSProperties = {
  color: '#93c5fd',
  fontSize: '13px',
  marginTop: '4px',
  lineHeight: '1.4',
}

const primaryBtnStyle: React.CSSProperties = {
  backgroundColor: '#ffffff',
  color: '#051C36',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
}

const secondaryBtnStyle: React.CSSProperties = {
  backgroundColor: '#1e3a5f',
  color: '#ffffff',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
}

const skipBtnStyle: React.CSSProperties = {
  backgroundColor: '#374151',
  color: '#d1d5db',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
}

export function PWAInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showInstallBanner, setShowInstallBanner] = useState(false)
  const [isIOS, setIsIOS] = useState(false)

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(
          () => {
            registration.update()
          },
          60 * 60 * 1000
        )
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error)
    },
  })

  useEffect(() => {
    const ios =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    setIsIOS(ios)

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true

    if (isStandalone) return

    // If user permanently skipped, never show again
    const skipped = localStorage.getItem('pwa-install-skipped')
    if (skipped) return

    const handler = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
      setShowInstallBanner(true)
    }

    window.addEventListener('beforeinstallprompt', handler)

    if (ios && !isStandalone) {
      setShowInstallBanner(true)
    }

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const handleInstall = async () => {
    if (!installPrompt) return
    installPrompt.prompt()
    const result = await installPrompt.userChoice
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false)
    }
    setInstallPrompt(null)
  }

  // "Not now" — hides for this session, comes back on refresh
  const dismissInstallBanner = () => {
    setShowInstallBanner(false)
  }

  // "Skip" — permanently hides, never comes back
  const skipInstallBanner = () => {
    setShowInstallBanner(false)
    localStorage.setItem('pwa-install-skipped', 'true')
  }

  // Update available banner
  if (needRefresh) {
    return (
      <div style={bannerStyle}>
        <button
          onClick={() => setNeedRefresh(false)}
          style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          aria-label="Dismiss"
        >
          <X size={18} color="#bfdbfe" />
        </button>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '15px', fontWeight: 600, margin: 0, color: '#ffffff' }}>
            Update available
          </p>
          <p style={subtextStyle}>A new version of BritFiler is ready.</p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'center' }}>
            <button onClick={() => updateServiceWorker(true)} style={primaryBtnStyle}>
              Update now
            </button>
            <button onClick={() => setNeedRefresh(false)} style={secondaryBtnStyle}>
              Later
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Install banner
  if (!showInstallBanner) return null

  return (
    <div style={bannerStyle}>
      <button
        onClick={dismissInstallBanner}
        style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
        aria-label="Dismiss"
      >
        <X size={18} color="#bfdbfe" />
      </button>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '15px', fontWeight: 600, margin: 0, color: '#ffffff' }}>
          Install BritFiler
        </p>
        {isIOS ? (
          <p style={subtextStyle}>
            Tap the share button then &quot;Add to Home Screen&quot; to install.
          </p>
        ) : (
          <p style={subtextStyle}>Add BritFiler to your home screen for quick access.</p>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'center' }}>
          {!isIOS && installPrompt && (
            <button onClick={handleInstall} style={primaryBtnStyle}>
              Install
            </button>
          )}
          <button onClick={dismissInstallBanner} style={secondaryBtnStyle}>
            Not now
          </button>
          <button onClick={skipInstallBanner} style={skipBtnStyle}>
            Skip
          </button>
        </div>
      </div>
    </div>
  )
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}
